import { createAsyncThunk, createDraftSafeSelector, createSlice } from "@reduxjs/toolkit";
import BigNumber from "bignumber.js";
import { getCurrentContractAddresses } from "src/configs/address";
import { BASE18, FS, RENT_CONTRACT_TIME_BASE } from "src/configs/constance";
import kawaiiArtData from "src/configs/kwaii";
import NFTAuctionContract from "src/contract-logic/logic/NFTAuctionContract";
import NFTBuyContract from "src/contract-logic/logic/NFTBuyContract";
import NFTCollectionContract from "src/contract-logic/logic/NFTCollectionContract";
import NFTCoreContract from "src/contract-logic/logic/NFTCoreContract";
import NFTMarketplaceCollectionContract from "src/contract-logic/logic/NFTMarketplaceCollectionContract";
import NFTRentContract from "src/contract-logic/logic/NFTRentContract";
import NFTSellContract from "src/contract-logic/logic/NFTSellContract";
import VeTravaContract from "src/contract-logic/logic/VeTravaContract";
import VeTravaMarketPlaceContract from "src/contract-logic/logic/VeTravaMarketPlaceContract";
import { resetAccountState } from "src/redux/account-slice";
import { capitalizeFirstLetter, getVeTravaRank, isNullAddress, shuffleArray } from "src/utils";
import multiCall from "src/utils/multicall";
import knightAssetsMapping, { rarities, types } from "src/utils/old-version-knight";
import ApiQuery from "src/utils/query/api-query";
import AuctionMarketplace from "src/utils/query/onchain/multi-call-query/auction-marketplace-query";
import SellingMarketplaceQuery from "src/utils/query/onchain/multi-call-query/selling-marketplace-query";

const client = new ApiQuery("http://178.128.216.246:3456");

export const fetchNFTRentalList = createAsyncThunk("nftMarketSlice/fetchNFTRentalList", async (_, thunkAPI) => {
  const currentOwnerAddress = thunkAPI.getState().accountSlice.address;
  if (currentOwnerAddress) thunkAPI.dispatch(fetchBatchNFTRentalList({ ownerAddresses: [currentOwnerAddress] }));

  const ownerSize = await NFTRentContract.getOwnerSize();
  let ownerAddresses = [];
  for (let index = 0; index < ownerSize; ++index) {
    const addr = await NFTRentContract.getOwnerAddressAtIndex(index);
    if (addr.toLowerCase() !== currentOwnerAddress?.toLowerCase()) ownerAddresses.push(addr);
    if (ownerAddresses.length >= 5) {
      await thunkAPI.dispatch(fetchBatchNFTRentalList({ ownerAddresses }));
      ownerAddresses = [];
    }
    if (thunkAPI.getState().nftMarketSlice.nftList?.rent?.length > 200) break;
  }
  if (ownerAddresses.length > 0) thunkAPI.dispatch(fetchBatchNFTRentalList({ ownerAddresses }));
});

export const fetchBatchNFTRentalList = createAsyncThunk(
  "nftMarketSlice/fetchBatchNFTRentalList",
  async ({ ownerAddresses }, thunkAPI) => {
    let res = [];
    for (let ownerAddress of ownerAddresses) {
      const ownerNFTRentListing = await NFTRentContract.getAllNFTRentListingOfOwner(ownerAddress);
      res = res.concat(ownerNFTRentListing);
      if (res.length >= 5) {
        thunkAPI.dispatch(pushNFT(res));
        res = [];
      }
    }
    if (res.length > 0) thunkAPI.dispatch(pushNFT(res));
  }
);

export const fetchNFTRentalListV2 = createAsyncThunk("nftMarketSlice/fetchNFTRentalListV2", async (_, thunkAPI) => {
  const ownerSize = await NFTRentContract.getOwnerSize();
  const [listOwner] = await Promise.all([
    multiCall(
      NFTRentContract.ABI,
      new Array(parseInt(ownerSize))
        .fill(1)
        .map((_, idx) => ({ address: NFTRentContract.ADDRESS, name: "ownerAddressAtIndex", params: [idx] }))
    ),
  ]);
  let listOwnerFlatted = listOwner.flat();

  const [tokenListSizes] = await Promise.all([
    multiCall(
      NFTRentContract.ABI,
      listOwnerFlatted.map((ownerAddress) => ({
        address: NFTRentContract.ADDRESS,
        name: "tokenListSize",
        params: [ownerAddress],
      }))
    ),
  ]);

  const listData = [];
  tokenListSizes.flat().map((tokenSize, index) => {
    for (let i = 0; i < tokenSize; i++) {
      listData.push({ wallet: listOwnerFlatted[index], index: i });
    }
  });
  shuffleArray(listData);

  let i;
  let j;
  let temporary;
  let chunk = 450;
  let tokenIdListChunkPromises = [];

  for (i = 0, j = listData.length; i < j; i += chunk) {
    temporary = listData.slice(i, i + chunk);
    tokenIdListChunkPromises.push(thunkAPI.dispatch(fetchBatchNFTRentalListV2({ temporary })));
  }
  await Promise.all(tokenIdListChunkPromises);
});

export const fetchBatchNFTRentalListV2 = createAsyncThunk(
  "nftMarketSlice/fetchBatchNFTRentalListV2",
  async ({ temporary }, thunkAPI) => {
    await Promise.all([
      multiCall(
        NFTRentContract.ABI,
        temporary.map((item) => ({
          address: NFTRentContract.ADDRESS,
          name: "tokenIdAtIndex",
          params: [item.wallet, item.index],
        }))
      ),
    ]).then(([tokenIdList]) => {
      return Promise.all([
        multiCall(
          NFTRentContract.ABI,
          temporary.map((item, idx) => ({
            address: NFTRentContract.ADDRESS,
            name: "checkRentListing",
            params: [tokenIdList[idx][0].toNumber(), item.wallet],
          }))
        ),
        multiCall(
          NFTRentContract.ABI,
          temporary.map((item, idx) => ({
            address: NFTRentContract.ADDRESS,
            name: "getAvailableStock",
            params: [item.wallet, tokenIdList[idx][0].toNumber()],
          }))
        ),
      ]).then(([nftRentListing, availableStock]) => {
        const data = nftRentListing.flat().map((item, idx) => ({
          isBnB: item.isBnB,
          isAvailableForRent: item.isAvailableForRent,
          minTime: parseInt(parseInt(item.minTime) / RENT_CONTRACT_TIME_BASE),
          maxTime: parseInt(parseInt(item.maxTime) / RENT_CONTRACT_TIME_BASE),
          currentOwner: temporary[idx].wallet,
          price: parseFloat(item.price) / BASE18,
          id: tokenIdList[idx][0].toNumber(),
          ...kawaiiArtData[parseInt(tokenIdList[idx][0])],
          available: parseInt(availableStock[idx][0]),
        }));
        thunkAPI.dispatch(pushNFT(data));
      });
    });
  }
);

export const fetchKawaiiNFTRentalDetail = createAsyncThunk(
  "nftMarketSlice/fetchKawaiiNFTRentalDetail",
  async ({ tokenId, ownerAddress }) => {
    const res = await NFTRentContract.getTokenMetadata(tokenId, ownerAddress);
    return res;
  }
);

export const fetchLimitSellingList = createAsyncThunk("nftMarketSlice/fetchLimitSellingList", async () => {
  return await SellingMarketplaceQuery.fetchLimited();
});

export const fetchTravaSellingList = createAsyncThunk("nftMarketSlice/fetchTravaSellingList", async () => {
  // if (environment == "mainnet") return await SellingGraphQuery.fetchData();
  // else return await SellingMarketplaceQuery.fetchNormal();
  return await SellingMarketplaceQuery.fetchNormal();
});

export const fetchTravaNFTSellingDetail = createAsyncThunk(
  "nftMarketSlice/fetchNFTSellingDetail",
  async ({ tokenId }) => {
    const [tokenOrder, tokenMetadata] = await Promise.all([
      NFTSellContract.getTokenOrder(tokenId),
      NFTCoreContract.getTokenMetadata(tokenId),
    ]);
    if (isNullAddress(tokenOrder.seller)) throw new Error("Item not found");
    return { id: tokenId, type: tokenMetadata.type, ...tokenOrder, ...tokenMetadata };
  }
);

export const fetchVeTravaSelling = createAsyncThunk("nftMarketSlice/fetchVeTravaSelling", async (_) => {
  const tokenOnMarket = await VeTravaMarketPlaceContract.getTokenOnSaleCount();
  const [tokenIdsOnMarket] = await Promise.all([
    multiCall(
      VeTravaMarketPlaceContract.ABI,
      new Array(tokenOnMarket).fill(1).map((_, idx) => ({
        address: VeTravaMarketPlaceContract.ADDRESS,
        name: "getTokenOnSaleAtIndex",
        params: [idx],
      }))
    ),
  ]);
  const tokenOnSaleFlattened = tokenIdsOnMarket.flat();
  const [tokensMetadata, tokenVotingPower, tokenOrder] = await Promise.all([
    multiCall(
      VeTravaContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({ address: VeTravaContract.ADDRESS, name: "locked", params: [tokenId] }))
    ),
    multiCall(
      VeTravaContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({
        address: VeTravaContract.ADDRESS,
        name: "balanceOfNFT",
        params: [tokenId],
      }))
    ),
    multiCall(
      VeTravaMarketPlaceContract.ABI,
      tokenOnSaleFlattened.map((tokenId) => ({
        address: VeTravaMarketPlaceContract.ADDRESS,
        name: "getTokenOrder",
        params: [tokenId],
      }))
    ),
  ]);
  const tokenOrdersFlattened = tokenOrder.flat();
  const { TRAVA_VE_TOKEN_ADDRESS, RTRAVA_VE_TOKEN_ADDRESS } = getCurrentContractAddresses();
  const res = tokensMetadata.map((item, idx) => {
    const id = parseInt(tokenOnSaleFlattened[idx]);
    const amount = BigNumber(item.amount._hex).dividedBy(BASE18).toNumber();
    const rwAmount = BigNumber(item.rewardAmount._hex).dividedBy(BASE18).toNumber();
    const end = new Date(parseInt(item.end * 1000));
    const lockEnd = end.getDate() + "/" + (end.getMonth() + 1) + "/" + end.getFullYear();
    const token =
      item.token === TRAVA_VE_TOKEN_ADDRESS ? "TRAVA" : item.token === RTRAVA_VE_TOKEN_ADDRESS ? "rTRAVA" : "BNB/TRAVA";
    const baseAmount = BigNumber(item.baseAmount._hex).dividedBy(BASE18).toNumber();
    const votingPower = BigNumber(parseInt(tokenVotingPower[idx])).dividedBy(BASE18).toNumber();
    const rank = getVeTravaRank(votingPower);
    const seller = tokenOrdersFlattened[idx].nftSeller || null;
    const price = BigNumber(tokenOrdersFlattened[idx].price._hex).dividedBy(BASE18).toNumber();
    const paymentOption = BigNumber(tokenOrdersFlattened[idx].paymentOption._hex).toNumber();
    return { amount, rwAmount, lockEnd, token, baseAmount, votingPower, id, rank, seller, price, paymentOption };
  });
  return res;
});

export const fetchVeTravaDetail = createAsyncThunk("nftMarketSlice/fetchVeTravaDetail", async ({ tokenId }) => {
  const [tokenOrder, tokenMetadata, tokenVotingPower] = await Promise.all([
    VeTravaMarketPlaceContract.getTokenOrder(tokenId),
    VeTravaContract.locked(tokenId),
    VeTravaContract.getVotingPower(tokenId),
  ]);
  return { ...tokenOrder, ...tokenMetadata, votingPower: tokenVotingPower, rank: getVeTravaRank(tokenVotingPower) };
});

export const fetchDetailLimitSelling = createAsyncThunk(
  "nftMarketSlice/fetchDetailLimitSelling",
  async ({ tokenId }) => {
    const [tokenOrder, cid, exp, owner] = await Promise.allSettled([
      NFTMarketplaceCollectionContract.getTokenOrder(tokenId),
      NFTCollectionContract.getCollectionURL(tokenId),
      NFTCollectionContract.getCollectionExperience(tokenId),
      NFTCollectionContract.ownerOf(tokenId),
    ]);
    if (!cid.value || cid.status == "rejected") throw new Error("Item not found");
    if (isNullAddress(tokenOrder.value.seller)) {
      return {
        id: tokenId,
        exp: parseInt(exp.value),
        seller: owner.status == "fulfilled" ? owner.value : NFTMarketplaceCollectionContract.ADDRESS,
        isSoldOut: true,
        isSpecial: true,
      };
    }
    return { ...tokenOrder.value, exp: exp.value };
  }
);

export const updateNFTRentalOnMarket = createAsyncThunk(
  "nftMarketSlice/updateNFTRentalOnMarket",
  async ({ index }, thunkAPI) => {
    const nft = thunkAPI.getState().nftMarketSlice.nftList.rent[index];
    const tokenId = nft.id,
      ownerAddress = nft.currentOwner;
    const data = await NFTRentContract.getNFTRentListing(tokenId, ownerAddress);
    const available = await NFTRentContract.getAvailableStock(ownerAddress, tokenId);
    return {
      ...nft,
      isBnB: data.isBnB,
      isAvailableForRent: data.isAvailableForRent,
      minTime: parseInt(parseInt(data.minTime) / RENT_CONTRACT_TIME_BASE),
      maxTime: parseInt(parseInt(data.maxTime) / RENT_CONTRACT_TIME_BASE),
      price: parseFloat(BigNumber(data.price).dividedBy(BASE18).toFixed()),
      available,
    };
  }
);

export const fetchAuctionHistoryList = createAsyncThunk("nftMarketSlice/fetchAuctionHistoryList", async (params) => {
  return await AuctionMarketplace.fetchHistoryList(params);
});

export const fetchTravaAuctionList = createAsyncThunk("nftMarketSlice/fetchTravaAuctionList", async () => {
  return await AuctionMarketplace.fetchList();
});

export const fetchNewRecordDetailAuction = createAsyncThunk(
  "nftMarketSlice/fetchNewRecordDetailAuction",
  async ({ tokenId }) => {
    const token = await NFTAuctionContract.getTokenOrder(tokenId);
    return {
      currentBid: BigNumber(token.currentBid).dividedBy(BASE18).toNumber(),
      endTime: parseInt(token.endTime) * 1000,
      nftSeller: token.nftSeller,
      bidSteps: parseInt(token.bidSteps),
    };
  }
);

export const fetchNewRecordListAuction = createAsyncThunk(
  "nftMarketSlice/fetchNewRecordListAuction",
  async ({ tokenIds }) => {
    let auctions = await multiCall(
      NFTAuctionContract.ABI,
      tokenIds.map((collectionId) => ({
        address: NFTAuctionContract.ADDRESS,
        name: "getTokenOrder",
        params: [collectionId],
      }))
    );
    auctions = auctions.flat();
    return auctions
      .map((item) => ({
        startingBid: BigNumber(item.startingBid._hex).dividedBy(BASE18).toNumber(),
        currentBid: BigNumber(item.currentBid._hex).dividedBy(BASE18).toNumber(),
        endTime: parseInt(item.endTime) * 1000,
        bidSteps: parseInt(item.bidSteps),
      }))
      .map((i) => ({ ...i, price: i.currentBid || i.startingBid }));
  }
);

export const fetchTravaBuyingList = createAsyncThunk("nftMarketSlice/fetchTravaBuyingList", async (_, thunkAPI) => {
  const total = await NFTBuyContract.getBuyOrderCount();
  thunkAPI.dispatch(updateTotalBuyingNFTs({ total }));
  const [buyOrderIds] = await Promise.all([
    multiCall(
      NFTBuyContract.ABI,
      new Array(total).fill(1).map((_, idx) => ({
        address: NFTBuyContract.ADDRESS,
        name: "getBuyOrderAtIndex",
        params: [idx],
      }))
    ),
  ]);
  const buyOrderIdsFlattened = buyOrderIds.flat();
  shuffleArray(buyOrderIdsFlattened);

  let chunk = 500,
    listChunkPromises = [],
    n = buyOrderIdsFlattened.length;
  for (let i = 0; i < n; i += chunk) {
    let buyOrderIdSlice = buyOrderIdsFlattened.slice(i, i + chunk);
    listChunkPromises.push(thunkAPI.dispatch(fetchBatchTravaNFTBuying({ buyOrderIdSlice })));
  }
  await Promise.all(listChunkPromises);
});

export const fetchBatchTravaNFTBuying = createAsyncThunk(
  "nftMarketSlice/fetchBatchTravaNFTBuying",
  async ({ buyOrderIdSlice }) => {
    const buyOrders = await multiCall(
      NFTBuyContract.ABI,
      buyOrderIdSlice.map((idx) => ({
        address: NFTBuyContract.ADDRESS,
        name: "getBuyOrder",
        params: [idx],
      }))
    );

    const buyOrdersFlattened = buyOrders.flat();
    return buyOrdersFlattened.map((item, idx) => {
      const rarity = rarities[parseInt(item.tokenRarity)];
      const type = types[parseInt(item.tokenType)];
      const exp = parseInt(item.experienceRequirement);
      const price = BigNumber(item.price._hex).dividedBy(BASE18).toNumber();
      const itemConfig = knightAssetsMapping["genesis"][rarity];
      const id = parseInt(buyOrderIdSlice[idx]);
      return {
        ...itemConfig["units"][type],
        badge: itemConfig.badge,
        palette: itemConfig.palette,
        name: capitalizeFirstLetter(type),
        id,
        rarity,
        tokenRarity: parseInt(item.tokenRarity),
        type,
        exp,
        price,
        buyerAddress: item.nftBuyer,
      };
    });
  }
);

export const fetchTravaBuyOrderDetail = createAsyncThunk(
  "nftMarketSlice/fetchTravaBuyOrderDetail",
  async ({ orderId }) => {
    const order = await NFTBuyContract.getBuyOrder(orderId);
    const rarity = rarities[parseInt(order.tokenRarity)];
    const type = types[parseInt(order.tokenType)];
    const exp = parseInt(order.experienceRequirement);
    const price = BigNumber(order.price).dividedBy(BASE18).toNumber();
    const itemConfig = knightAssetsMapping["genesis"][rarity];
    return {
      ...itemConfig["units"][type],
      badge: itemConfig.badge,
      palette: itemConfig.palette,
      name: capitalizeFirstLetter(type),
      id: parseInt(orderId),
      rarity,
      type,
      exp,
      price,
      buyerAddress: order.nftBuyer,
    };
  }
);

export const fetchAuctionDetail = createAsyncThunk("nftMarketSlice/fetchAuctionDetail", async ({ knightId }) => {
  return await AuctionMarketplace.fetchAuctionDetail(knightId);
});

export const fetchUserWallet = createAsyncThunk("nftMarketSlice/saleNFT", async ({ address }) => {
  const res = await client.get(`/api/v1/wallet/${address}`);
  return res;
});

export const addUserWallet = createAsyncThunk("nftMarketSlice/addUserWallet", async ({ data }) => {
  const res = await client.post("/api/v1/wallet", data);
  return res;
});

export const updateUserWallet = createAsyncThunk("nftMarketSlice/updateUserWallet", async ({ data }) => {
  const res = await client.post("/api/v1/wallet", data);
  return res;
});

export const fetchNFTRentingListOfOwner = createAsyncThunk(
  "nftMarketSlice/fetchNFTRentingListOfOwner",
  async ({ ownerAddress }) => {
    const res = await NFTRentContract.getRentingListOfOwner(ownerAddress);
    return res;
  }
);

const initialState = {
  rentList: undefined,
  sell: { v1: [], v2: [] },
  limitedSell: [],
  buy: undefined,
  fetchNFTRentalListStatus: FS.INITIAL,
  fetchTravaSellingStatus: FS.INITIAL,
  fetchTravaBuyingStatus: FS.INITIAL,
  fetchTravaAuctionStatus: FS.INITIAL,
  fetchVeTravaSellingStatus: FS.INITIAL,
  userWallet: undefined,
  itemDetail: undefined,
  buyOrderDetail: undefined,
  auction: { v1: [], v2: [], specials: [] },
  auctionDetail: undefined,
  vetrava: undefined,
  vetravaDetail: undefined,
  historyAuction: { loading: false, list: { v1: [], v2: [], specials: [], total: 0 } },
};

const nftMarketSlice = createSlice({
  name: "nftMarketSlice",
  initialState,
  reducers: {
    pushNFT(state, action) {
      if (!state.rentList) {
        state.rentList = action.payload;
        state.fetchNFTRentalListStatus = FS.SUCCESS;
      } else state.rentList = state.rentList.concat(action.payload);
    },
    updateTotalSellingNFTs(state, action) {
      state.sell = { ...state.sell, ...action.payload };
    },
    updateTotalBuyingNFTs(state, action) {
      state.buy = { ...state.buy, ...action.payload };
    },
    updateTotalAuctions(state, action) {
      state.auction = { ...state.auction, ...action.payload };
    },
    resetMarket(state, _) {
      Object.assign(state, initialState);
    },
    removeItemDetail(state, _) {
      state.itemDetail = undefined;
    },
    removeSellingItemOnMarket(state, action) {
      const tokenId = action.payload.tokenId;
      const sellList = state.sell?.list;
      if (sellList) {
        const index = sellList.findIndex((item) => item.id === parseInt(tokenId));
        if (index < 0) return state;
        state.sell.list = sellList.slice(0, index).concat(sellList.slice(index + 1));
        state.sell.total -= 1;
      }
    },
    removeBuyingItemOnMarket(state, action) {
      const orderId = action.payload.orderId;
      const buyList = state.buy?.list;
      if (buyList) {
        const index = buyList.findIndex((i) => i.id == orderId);
        if (index > -1) {
          state.buy.list.splice(index, 1);
          state.buy.total -= 1;
        }
      }
    },
    removeAuctionItemOnMarket(state, action) {
      const { tokenId } = action.payload;
      const auctionList = state.auction?.list || [];
      const index = auctionList.findIndex((i) => i.id == tokenId);
      if (index > 1) {
        state.auction.list.splice(index, 1);
        state.auction.total -= 1;
      }
    },
    removeVeTravaOnMarket(state, action) {
      const { tokenId } = action.payload;
      const veTravaList = state.vetrava || [];
      const index = veTravaList.findIndex((i) => i.id == tokenId);
      if (index > -1) state.vetrava.splice(index, 1);
    },
    resetRentMarket(state, _) {
      state.rentList = undefined;
      state.fetchNFTRentalListStatus = FS.INITIAL;
    },
  },
  extraReducers: (builder) =>
    builder
      .addCase(fetchUserWallet.fulfilled, (state, action) => {
        state.userWallet = action.payload;
      })
      // for-rent
      .addCase(fetchNFTRentalListV2.pending, (state, _) => {
        state.fetchNFTRentalListStatus = FS.FETCHING;
      })
      .addCase(fetchNFTRentalListV2.fulfilled, (state) => {
        state.fetchNFTRentalListStatus = FS.SUCCESS;
      })
      .addCase(fetchNFTRentalListV2.rejected, (state) => {
        state.fetchNFTRentalListStatus = FS.FAIL;
      })
      .addCase(updateNFTRentalOnMarket.fulfilled, (state, action) => {
        const index = action.meta.arg.index;
        state.rentList[index] = action.payload;
      })

      .addCase(fetchTravaSellingList.fulfilled, (state, action) => {
        state.sell = action.payload;
      })
      .addCase(fetchTravaSellingList.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchTravaNFTSellingDetail.fulfilled, (state, action) => {
        state.itemDetail = action.payload;
      })
      .addCase(fetchTravaNFTSellingDetail.rejected, (state, action) => {
        state.itemDetail = undefined;
        console.error(action);
      })
      .addCase(fetchBatchTravaNFTBuying.fulfilled, (state, action) => {
        if (!state.buy.list) {
          state.buy.list = action.payload;
          state.fetchTravaBuyingStatus = FS.SUCCESS;
        } else state.buy.list = state.buy.list.concat(action.payload);
      })
      .addCase(fetchTravaAuctionList.fulfilled, (state, action) => {
        state.auction = action.payload;
        state.fetchTravaAuctionStatus = FS.SUCCESS;
      })
      .addCase(fetchTravaBuyingList.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchTravaAuctionList.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchVeTravaSelling.fulfilled, (state, action) => {
        if (!state.vetrava) {
          state.vetrava = action.payload;
          state.fetchVeTravaSellingStatus = FS.SUCCESS;
        } else state.vetrava = state.vetrava.concat(action.payload);
      })
      .addCase(fetchVeTravaSelling.rejected, (state, action) => {
        console.error(action);
      })
      .addCase(fetchVeTravaDetail.fulfilled, (state, action) => {
        state.vetravaDetail = action.payload;
      })
      .addCase(fetchVeTravaDetail.rejected, (state) => {
        state.vetravaDetail = undefined;
      })
      .addCase(fetchAuctionHistoryList.fulfilled, (state, action) => {
        state.historyAuction.list = action.payload;
        state.historyAuction.loading = false;
      })
      .addCase(fetchAuctionHistoryList.pending, (state) => {
        state.historyAuction = { ...state.historyAuction, loading: true };
      })
      .addCase(fetchAuctionHistoryList.rejected, (state) => {
        state.historyAuction.list = { v1: [], v2: [], specials: [], total: 0 };
        state.historyAuction.loading = false;
      })
      .addCase(fetchTravaBuyOrderDetail.fulfilled, (state, action) => {
        state.buyOrderDetail = action.payload;
      })
      .addCase(fetchAuctionDetail.fulfilled, (state, action) => {
        state.auctionDetail = action.payload;
      })
      .addCase(fetchAuctionDetail.rejected, (state) => {
        state.auctionDetail = undefined;
      })
      .addCase(fetchKawaiiNFTRentalDetail.fulfilled, (state, action) => {
        state.itemDetail = action.payload;
      })
      .addCase(fetchKawaiiNFTRentalDetail.rejected, (state, action) => {
        state.itemDetail = undefined;
        console.error(action);
      })
      .addCase(fetchDetailLimitSelling.fulfilled, (state, action) => {
        state.itemDetail = action.payload;
      })
      .addCase(fetchDetailLimitSelling.rejected, (state, action) => {
        state.itemDetail = undefined;
        console.error(action);
      })
      .addCase(fetchLimitSellingList.fulfilled, (state, action) => {
        state.limitedSell = action.payload;
      })
      .addCase(fetchLimitSellingList.rejected, (state, action) => {
        console.error(action);
        state.limitedSell = undefined;
      })
      .addCase(resetAccountState, (state, _) => {
        state.userWallet = undefined;
      })
      .addCase(fetchNewRecordDetailAuction.fulfilled, (state, action) => {
        state.auctionDetail = { ...state.auctionDetail, ...action.payload };
      }),
});

export default nftMarketSlice.reducer;
export const {
  pushNFT,
  updateTotalSellingNFTs,
  updateTotalBuyingNFTs,
  updateTotalAuctions,
  resetMarket,
  resetRentMarket,
  removeItemDetail,
  removeSellingItemOnMarket,
  removeBuyingItemOnMarket,
  resetAuctions,
  removeVeTravaOnMarket,
  removeAuctionItemOnMarket,
} = nftMarketSlice.actions;

export const selectNFTMarketSlice = (state) => state.nftMarketSlice;

export const selectNFTRentalInMarket = createDraftSafeSelector(
  (state) => selectNFTMarketSlice(state).rentList,
  (list) => {
    if (!list) return null;
    return list.filter((item) => item.available > 0 && item.isAvailableForRent);
  }
);

export const selectNFTBuyingInMarket = createDraftSafeSelector(
  (state) => selectNFTMarketSlice(state).buy,
  (buy) => {
    if (!buy) return null;
    return buy.list || [];
  }
);

export const selectVeTravaInMarket = createDraftSafeSelector(
  (state) => selectNFTMarketSlice(state).vetrava,
  (vetrava) => {
    if (!vetrava) return null;
    else return vetrava || [];
  }
);
